import { useContext, useMemo } from "react";

import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import {
    CofConfigId,
    LensCreatorPayoutEligibility,
    type MyLensesLens,
    SurfaceType,
    Visibility,
} from "../../../gql-generated/types";
import { UserInfoContext } from "../../../shared/api/contexts";
import { useSubmissionQuotas } from "../../../shared/hooks/submission-quotas";
import { type SubmissionQuota, type SubmissionQuotas } from "../../../shared/models/SubmissionQuota";
import { type LensPreviewMediaItem, lensPreviewMediaItemsAtomById } from "../../../shared/state";
import { useSubmitLensConfiguration } from "../../../submit/hooks";

import { type SubmitLensState } from "src/app/submit/models";

interface UsePublishModalStateProps {
    surfaceType: readonly SurfaceType[];
    lensForUpdate?: Pick<MyLensesLens, "distributionsList" | "lensCreatorPayoutEligibility">;
    visibility?: Visibility;
    lensOrSubmissionId: string;
}

interface UsePublishModalStateReturn {
    isSpectaclesOnly: boolean;
    isSnapchatOnly: boolean;
    username: string | undefined;
    isMyLensesCreatorPayoutEnabled: boolean;
    ownerMismatch: boolean;
    isMonetizationDisabled: boolean | undefined;
    submitLensState: SubmitLensState;
    setIsExclusiveLens: (isExclusive: boolean) => void;
    previewMediaItems: LensPreviewMediaItem[];
    userLoading: boolean;
    isExclusiveLensChecked: boolean;
    regularSubmissionQuota: SubmissionQuota | null;
    lensPlusSubmissionQuota: SubmissionQuota | null;
    refetchSubmissionQuotas: () => Promise<SubmissionQuotas>;
}

export const usePublishModalState = ({
    surfaceType,
    lensForUpdate,
    visibility,
    lensOrSubmissionId,
}: UsePublishModalStateProps): UsePublishModalStateReturn => {
    const userInfo = useContext(UserInfoContext);
    const { configs } = useFeatureGates();
    const { submitLensState, setIsExclusiveLens } = useSubmitLensConfiguration();
    const previewMediaItems = useRecoilValue(lensPreviewMediaItemsAtomById(lensOrSubmissionId));
    const {
        regular: regularSubmissionQuota,
        lensPlus: lensPlusSubmissionQuota,
        refetch: refetchSubmissionQuotas,
    } = useSubmissionQuotas();

    return useMemo(() => {
        const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
        const isSnapchatOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSnapchat);
        const username = userInfo?.data?.getUserProfile.userDisplayName;
        const isMyLensesCreatorPayoutEnabled = configs[CofConfigId.MyLensesCreatorPayout].enabled;

        // Calculate owner mismatch
        const lensForUpdateDistributionsList = lensForUpdate?.distributionsList;
        const ownerMismatch =
            !lensForUpdateDistributionsList?.some(
                (distribution) => distribution.destinationId === userInfo?.data?.getUserProfile.userId,
            ) &&
            lensForUpdate?.lensCreatorPayoutEligibility ===
                LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset;

        // Calculate monetization disabled state
        const isMonetizationDisabled =
            visibility === Visibility.VisibilityDontPublishToSnapchat ||
            (lensForUpdate &&
                lensForUpdate.lensCreatorPayoutEligibility !==
                    LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset);

        return {
            isSpectaclesOnly,
            isSnapchatOnly,
            username,
            isMyLensesCreatorPayoutEnabled,
            isExclusiveLensChecked: submitLensState.isExclusiveLens,
            ownerMismatch,
            isMonetizationDisabled,
            submitLensState,
            setIsExclusiveLens,
            previewMediaItems,
            userLoading: userInfo?.loading ?? false,
            regularSubmissionQuota,
            lensPlusSubmissionQuota,
            refetchSubmissionQuotas,
        };
    }, [
        surfaceType,
        userInfo,
        configs,
        lensForUpdate,
        visibility,
        submitLensState,
        setIsExclusiveLens,
        previewMediaItems,
        regularSubmissionQuota,
        lensPlusSubmissionQuota,
        refetchSubmissionQuotas,
    ]);
};
